import { useState } from 'react';
import { Zap, Target, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stockColors = {
  TSLA: { main: 'text-red-600', bg: 'bg-red-50' },
  AAPL: { main: 'text-zinc-700', bg: 'bg-zinc-50' },
  NVDA: { main: 'text-emerald-600', bg: 'bg-emerald-50' },
  QQQ: { main: 'text-blue-600', bg: 'bg-blue-50' }
};

const signalColors = {
  STRONG_BUY: 'bg-emerald-600 text-white border-emerald-600',
  BUY: 'bg-emerald-500 text-white border-emerald-500',
  NEUTRAL: 'bg-zinc-400 text-white border-zinc-400',
  SELL: 'bg-red-500 text-white border-red-500',
  STRONG_SELL: 'bg-red-600 text-white border-red-600'
};

export default function StockCard({ stock }) {
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);

  const isPositive = stock.change >= 0;
  const colors = stockColors[stock.ticker] || { main: 'text-zinc-700', bg: 'bg-zinc-50' };
  const signalColor = signalColors[stock.trade_signal] || signalColors.NEUTRAL;

  const getAIRecommendation = async () => {
    setLoadingAI(true);
    setShowAI(true);
    try {
      const response = await axios.get(`${API}/ai/recommend/${stock.ticker}`);
      setAiRecommendation(response.data);
    } catch (error) {
      console.error('Error getting AI recommendation:', error);
      setAiRecommendation({
        recommendation: '❌ Unable to get AI recommendation. Please try again.'
      });
    }
    setLoadingAI(false);
  };

  return (
    <>
      <div 
        className="stock-card bg-white border border-zinc-200 rounded-sm p-5 shadow-sm" 
        data-testid={`stock-card-${stock.ticker}`}
      >
        {/* Header with Signal */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-medium tracking-tight" style={{ fontFamily: 'Work Sans, sans-serif' }}>{stock.ticker}</h3>
            <p className="text-xs text-zinc-500">{stock.name}</p>
          </div>
          <Badge className={`text-xs font-mono ${signalColor}`} data-testid={`signal-${stock.ticker}`}>
            {stock.trade_signal.replace('_', ' ')}
          </Badge>
        </div>

        {/* Price */}
        <div className="mb-3">
          <div className="text-3xl font-mono font-medium tracking-tighter" data-testid={`price-${stock.ticker}`}>
            ${stock.current_price.toFixed(2)}
          </div>
          <div className={`text-sm font-mono ${isPositive ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`change-${stock.ticker}`}>
            {isPositive ? '+' : ''}{stock.change.toFixed(2)} ({stock.change_percent.toFixed(2)}%)
          </div>
        </div>

        {/* Professional Indicators */}
        <div className="space-y-2 pt-3 border-t border-zinc-100 mb-3">
          {/* RSI */}
          <div className="flex justify-between items-center text-xs" data-testid="indicator-RSI">
            <span className="text-zinc-500">RSI (14)</span>
            <div className="flex items-center gap-2">
              <span className={`font-mono ${stock.rsi < 30 ? 'text-emerald-600' : stock.rsi > 70 ? 'text-red-600' : 'text-zinc-700'}`}>
                {stock.rsi.toFixed(1)}
              </span>
              {stock.rsi < 30 && <Badge variant="default" className="text-xs bg-emerald-600">OVERSOLD</Badge>}
              {stock.rsi > 70 && <Badge variant="default" className="text-xs bg-red-600">OVERBOUGHT</Badge>}
            </div>
          </div>

          {/* MACD */}
          <div className="flex justify-between items-center text-xs" data-testid="indicator-MACD">
            <span className="text-zinc-500">MACD</span>
            <div className="flex items-center gap-2">
              <span className={`font-mono ${stock.macd_histogram > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {stock.macd_histogram.toFixed(2)}
              </span>
              {stock.macd_histogram > 0 && <Target className="w-3 h-3 text-emerald-600" />}
              {stock.macd_histogram < 0 && <Target className="w-3 h-3 text-red-600" />}
            </div>
          </div>

          {/* Volume */}
          <div className="flex justify-between items-center text-xs" data-testid="indicator-Volume">
            <span className="text-zinc-500">Volume</span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-zinc-700">{stock.volume_ratio.toFixed(2)}x</span>
              {stock.volume_ratio > 2 && (
                <Badge variant="default" className="text-xs bg-blue-600">
                  <Zap className="w-3 h-3 mr-0.5" />
                  SPIKE
                </Badge>
              )}
            </div>
          </div>

          {/* Support/Resistance */}
          <div className="flex justify-between items-center text-xs" data-testid="indicator-Support">
            <span className="text-zinc-500">Support</span>
            <span className="font-mono text-emerald-600">${stock.support_level.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-xs" data-testid="indicator-Resistance">
            <span className="text-zinc-500">Resistance</span>
            <span className="font-mono text-red-600">${stock.resistance_level.toFixed(2)}</span>
          </div>

          {/* Signal Strength */}
          {stock.signal_strength > 0 && (
            <div className="pt-2 border-t border-zinc-100">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">Signal Strength</span>
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div 
                      key={i} 
                      className={`h-2 w-2 rounded-full ${
                        i < stock.signal_strength 
                          ? stock.trade_signal.includes('BUY') ? 'bg-emerald-600' : 'bg-red-600'
                          : 'bg-zinc-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ask AI Button */}
        <Button
          onClick={getAIRecommendation}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
          size="sm"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Ask AI Advisor
        </Button>
      </div>

      {/* AI Recommendation Dialog */}
      <Dialog open={showAI} onOpenChange={setShowAI}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              🤖 AI Analysis: {stock.ticker}
            </DialogTitle>
          </DialogHeader>
          
          {loadingAI ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-sm text-zinc-600">AI is analyzing all data...</p>
              </div>
            </div>
          ) : aiRecommendation ? (
            <div className="space-y-4">
              <pre className="whitespace-pre-wrap text-sm font-sans bg-zinc-50 p-4 rounded-lg border border-zinc-200">
                {aiRecommendation.recommendation}
              </pre>
              
              {aiRecommendation.data_analyzed && (
                <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
                  <span>Analyzed:</span>
                  {aiRecommendation.data_analyzed.technical && <Badge variant="outline">Technical</Badge>}
                  {aiRecommendation.data_analyzed.options_flow && <Badge variant="outline">Options Flow</Badge>}
                  {aiRecommendation.data_analyzed.dark_pool && <Badge variant="outline">Dark Pool</Badge>}
                  {aiRecommendation.data_analyzed.market_context && <Badge variant="outline">Market Context</Badge>}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
